import {
  CELL_RENDERER,
  ILimit,
  IModalDialogOptions,
  IProduct,
  ListColumn,
  WIDGET_COMPONENT,
  XUX_GRID_ACTIONS,
} from '../../types';
import { merge } from 'lodash';
import { BTN_CANCEL, BTN_DONE } from '@text-content/button-templates';
import {
  DEFAULT_GRID_MIN_HEIGHT,
  WIDGET_FAMILY,
} from '@config/global-constants';
import { I18n } from '@app/i18n/strings-keys';
import { FormGroup } from '@angular/forms';
import { COLUMN_ALIGN_RIGHT } from '@app/shared/constants/column-align-definition';
import { HawkerSessionCloseOutService } from './hawker-session-close-out.service';
import {
  getColumnTipConfig,
  loadOrReturnedInputParams,
} from './hawker-session.utils';
import { buildInformationColumnConfig } from './hawker-session-service.config';
import {
  DEFAULT_INFORMATION_DATA,
  HAWKER_SESSION_FORM_FIELD_PATH,
} from './hawker-session.constant';
import { XVIHeaderIconRendererComponent } from '@app/components/xvi-grid/custom-components/header-icon-renderer/header-icon-renderer.component';
import { updateGridRowHeightNameWrap } from '@app/shared/helpers';
import { observableCombineLatest, rx } from '@app/shared/rx';

export interface IHawkerSessionCloseOutPreviewModalOptions {
  hawkerSessionId: string;
  hawkerSessionProducts: {
    activeProducts: IProduct[];
    inactiveProducts: IProduct[];
  };
  onFormReadyCB: (form: FormGroup) => void;
  beforeFormClose: Function;
  modalId?: string;
}

export const HawkerSessionCloseOutPreviewModal = (
  hawkerSessionCloseOutService: HawkerSessionCloseOutService,
  options: IHawkerSessionCloseOutPreviewModalOptions = {
    hawkerSessionId: '',
    hawkerSessionProducts: {
      activeProducts: [],
      inactiveProducts: [],
    },
    onFormReadyCB: () => {},
    beforeFormClose: () => {},
  },
): IModalDialogOptions => {
  const i18nService = hawkerSessionCloseOutService.i18nService;
  const loadLimit: ILimit<number> = {
    maximum: 9999,
    minimum: 0,
  };
  const buttons = [
    {
      ...BTN_DONE,
      caption: I18n.Forms.HawkerSessionForm.ConfirmToCloseOut,
      disabledObs: observableCombineLatest([
        hawkerSessionCloseOutService.isCloseOutPreviewGridBusy$,
        hawkerSessionCloseOutService.formValid$,
        hawkerSessionCloseOutService.getOrdersSummarySuccess$,
      ]).pipe(
        rx.map(
          ([isBusy, isValid, isSuccess]) => isBusy || !isValid || !isSuccess,
        ),
      ),
    },
    BTN_CANCEL,
  ];
  const gridIdentifyClass = 'close-out-preview-form-grid';

  return merge(
    {},
    {
      showLoader: true,
      title: i18nService.translate(I18n.Forms.HawkerSessionForm.CloseOutReview),
      subTitleObs: hawkerSessionCloseOutService.closeOutPreviewDescriptionObs,
      validation: true,
      modalClass: 'modal-hg hawker-session-close-out-preview-modal',
      buttons,
      value: {
        hawker_session_id: null,
        items: [],
        information_data: [DEFAULT_INFORMATION_DATA],
        original_items: [],
      },
      schema: {
        type: 'object',
        properties: {
          hawker_session_id: {
            type: 'string',
          },
          items: {
            type: 'array',
          },
          information_data: {
            type: 'array',
          },
          original_items: {
            type: 'array',
          },
        },
      },
      formRules: {
        fields: {
          hawker_session_id: {
            show: false,
          },
          original_items: {
            show: false,
          },
          items: {
            title: null,
            ignoreAutoTitleWhenTranslate: true,
            class: `${gridIdentifyClass} no-titled-grid`,
            action: hawkerSessionCloseOutService.closeOutPreviewGridSubject,
            widgetFamily: WIDGET_FAMILY.XUX,
            widget: WIDGET_COMPONENT.edit_grid_custom,
            widgetData: {
              displayCountObs: hawkerSessionCloseOutService.displayedRowCount$,
              showLoader:
                hawkerSessionCloseOutService.isCloseOutPreviewGridBusy$,
              showSearch: true,
              canAdd: false,
              canEdit: false,
              orderControls: false,
              canRemove: false,
              disableColumnSelector: true,
              multiselect: false,
              treeViewMode: false,
              defaultExpanded: false,
              gridMinHeight: DEFAULT_GRID_MIN_HEIGHT,
              userGridOptions: {
                deltaRowDataMode: false,
                suppressMovableColumns: true,
                suppressHorizontalScroll: false,
                suppressClickEdit: true,
                suppressRowClickSelection: true,
                suppressRowTransform: true,
                overlayNoRowsTemplate: i18nService.instant(
                  I18n.Common.NoRowsToShow,
                ),
              },
              columns: [
                {
                  title: I18n.EntityNames.Product,
                  path: 'name',
                  cellClass: 'cell-wrap-text',
                  headerComponentFramework: XVIHeaderIconRendererComponent,
                  autoHeight: true,
                  headerComponentParams: {
                    displayName:
                      hawkerSessionCloseOutService.i18nService.instant(
                        I18n.EntityNames.Product,
                      ),
                    headerIconClick: (isShrink) =>
                      updateGridRowHeightNameWrap({
                        isShrink,
                        gridIdentifyClass,
                        xuxGrid:
                          hawkerSessionCloseOutService.closeOutListXuxGrid,
                      }),
                  },
                  defaultView: true,
                  sortable: false,
                  filterParams: {
                    quickFilter: true,
                  },
                  minWidth: 120,
                },
                {
                  title: I18n.Forms.HawkerSessionForm.TotalQtyRequested,
                  path: 'total_requested',
                  headerClass: 'entry-form-text-wrapping',
                  defaultView: true,
                  width: 140,
                  minWidth: 140,
                  cellRenderer: CELL_RENDERER.float_input_renderer,
                  valueFormatter: (params) =>
                    hawkerSessionCloseOutService.numberFormatService.getNumberFormatter(
                      false,
                    )(params),
                  cellRendererParams: (): ListColumn['cellRendererParams'] => {
                    return {
                      ...loadOrReturnedInputParams({
                        min: loadLimit.minimum,
                      }),
                      max: loadLimit.maximum,
                    };
                  },
                },
                {
                  title: I18n.Forms.HawkerSessionForm.TotalQtyReturned,
                  path: 'total_returned',
                  defaultView: true,
                  width: 140,
                  minWidth: 140,
                  headerClass: 'entry-form-text-wrapping',
                  cellRenderer: CELL_RENDERER.float_input_renderer,
                  valueFormatter: (params) =>
                    hawkerSessionCloseOutService.numberFormatService.getNumberFormatter(
                      false,
                    )(params),
                  cellRendererParams: (): ListColumn['cellRendererParams'] => {
                    return {
                      ...loadOrReturnedInputParams({
                        min: loadLimit.minimum,
                      }),
                      max: loadLimit.maximum,
                    };
                  },
                },
                {
                  title: I18n.Forms.HawkerSessionForm.NetQtyTaken,
                  path: 'net_qty_taken',
                  defaultView: true,
                  ...COLUMN_ALIGN_RIGHT,
                  width: 120,
                  minWidth: 120,
                  headerComponentParams: getColumnTipConfig(
                    i18nService.instant(
                      I18n.Forms.HawkerSessionForm.TotalQtyDifference,
                    ),
                  ),
                  headerClass: 'entry-form-text-wrapping',
                  valueFormatter:
                    hawkerSessionCloseOutService.numberFormatService.getNumberFormatter(
                      false,
                    ),
                },
                {
                  title: I18n.Forms.StandWorksheetForm.TerminalQty,
                  path: 'terminal_quantity',
                  defaultView: true,
                  ...COLUMN_ALIGN_RIGHT,
                  width: 100,
                  minWidth: 100,
                  headerClass: 'entry-form-text-wrapping',
                  valueFormatter:
                    hawkerSessionCloseOutService.numberFormatService.getNumberFormatter(
                      false,
                    ),
                },
                {
                  title: I18n.Forms.StandWorksheetForm.VarianceQty,
                  path: 'variance_qty',
                  defaultView: true,
                  ...COLUMN_ALIGN_RIGHT,
                  width: 120,
                  minWidth: 120,
                  headerClass: 'entry-form-text-wrapping',
                  headerComponentParams: getColumnTipConfig(
                    i18nService.instant(
                      I18n.Forms.HawkerSessionForm
                        .VarianceQtyTipForHawkerSession,
                    ),
                  ),
                  valueFormatter:
                    hawkerSessionCloseOutService.numberFormatService.getNumberFormatter(
                      false,
                    ),
                },
                {
                  title: I18n.Forms.StandWorksheetForm.TerminalSales,
                  path: 'terminal_sales',
                  defaultView: true,
                  ...COLUMN_ALIGN_RIGHT,
                  width: 140,
                  minWidth: 140,
                  headerClass: 'entry-form-text-wrapping',
                  headerComponentParams: getColumnTipConfig(
                    i18nService.instant(
                      I18n.Forms.HawkerSessionForm
                        .TerminalSalesTipForHawkerSession,
                    ),
                  ),
                  valueFormatter: (params) =>
                    hawkerSessionCloseOutService.numberFormatService.getNumberFormatter(
                      true,
                    )(params),
                },
              ],
              inlineEditOptions: {
                beforeActionMappers: [
                  hawkerSessionCloseOutService.getCloseOutPreviewItemMapper(),
                ],
              },
              warningValidators: [
                hawkerSessionCloseOutService.getProductNotRequestedValidator(),
                hawkerSessionCloseOutService.getTotalReturnedQtyOverflowWarningValidator(),
              ],
              eventHandlers: {
                [XUX_GRID_ACTIONS.GRID_READY]:
                  hawkerSessionCloseOutService.listModalGridAction.bind(
                    hawkerSessionCloseOutService,
                  ),
              },
            },
            validators: [
              hawkerSessionCloseOutService.getQtyRequestedAndQtyReturnedEmptyValidator(),
            ],
          },
          information_data: {
            ...buildInformationColumnConfig(hawkerSessionCloseOutService)[
              HAWKER_SESSION_FORM_FIELD_PATH.virtualInformationData
            ],
          },
        },
      },
      onFormReady: options.onFormReadyCB,
      beforeFormClose: options.beforeFormClose,
      onFormClose: hawkerSessionCloseOutService.onModalClose.bind(
        hawkerSessionCloseOutService,
      ),
    },
  );
};

export const hawkerSessionCloseOutPreviewModalDefinition = {
  HawkerSessionCloseOutPreviewModal,
};
